import { createSlice } from '@reduxjs/toolkit';
import { isProductPriceHidden, lineTotal, originalUnitPrice } from '../utils/price';
import { loadCartItems } from './cartStorage';

export const DELIVERY_TYPES = {
  DELIVERY_ONLY: 'delivery_only',
  DELIVERY_INSTALL: 'delivery_install',
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: loadCartItems(),
    deliveryType: DELIVERY_TYPES.DELIVERY_ONLY,
  },
  reducers: {
    addOrUpdateItem(state, action) {
      const { product, quantity, note } = action.payload;
      if (isProductPriceHidden(product)) return;
      const index = state.items.findIndex((entry) => entry.product.id === product.id);
      if (index >= 0) {
        state.items[index].quantity = quantity;
        state.items[index].note = note ?? state.items[index].note;
        state.items[index].product = product;
      } else {
        state.items.push({ product, quantity, note: note ?? '' });
      }
    },
    setItemQuantity(state, action) {
      const { productId, quantity } = action.payload;
      const index = state.items.findIndex((entry) => entry.product.id === productId);
      if (index < 0) return;
      if (quantity <= 0) {
        state.items.splice(index, 1);
      } else {
        state.items[index].quantity = quantity;
      }
    },
    updateItemNote(state, action) {
      const { productId, note } = action.payload;
      const item = state.items.find((entry) => entry.product.id === productId);
      if (item) item.note = note;
    },
    removeFromCart(state, action) {
      const productId = action.payload;
      state.items = state.items.filter((entry) => entry.product.id !== productId);
    },
    clearCart(state) {
      state.items = [];
    },
    setDeliveryType(state, action) {
      state.deliveryType = action.payload;
    },
  },
});

export const {
  addOrUpdateItem,
  setItemQuantity,
  updateItemNote,
  removeFromCart,
  clearCart,
  setDeliveryType,
} = cartSlice.actions;

export const selectCartItems = (state) => state.cart.items;
export const selectDeliveryType = (state) => state.cart.deliveryType;
export const selectCartCount = (state) =>
  state.cart.items.reduce((sum, entry) => sum + entry.quantity, 0);
export const selectIsInCart = (productId) => (state) =>
  state.cart.items.some((entry) => entry.product.id === productId);
export const selectCartEntry = (productId) => (state) =>
  state.cart.items.find((entry) => entry.product.id === productId);
export const selectCartSubtotal = (state) =>
  state.cart.items.reduce((sum, entry) => sum + lineTotal(entry.product.price, entry.quantity), 0);
export const selectCartOriginalSubtotal = (state) =>
  state.cart.items.reduce(
    (sum, entry) => sum + originalUnitPrice(entry.product) * entry.quantity,
    0,
  );
export const selectCartDiscount = (state) => {
  const original = selectCartOriginalSubtotal(state);
  const subtotal = selectCartSubtotal(state);
  return Math.max(0, original - subtotal);
};

export default cartSlice.reducer;
